/**
 * Configuration centralisée des champs de recherche pour les entités
 * (auteurs, éditeurs, relieurs)
 *
 * Principe DRY : Un seul endroit pour définir les champs de chaque entité
 * Principe Factory Pattern : Functions pour récupérer les champs selon le type
 *
 * @module entityFields
 */

// ============================================================================
// Types
// ============================================================================

export type EntityType = 'auteurs' | 'editeurs' | 'relieurs' | 'lieux_edition';

// ============================================================================
// Configuration Auteurs
// ============================================================================

/**
 * Champs de base à récupérer pour l'index auteurs
 * Optimisé pour minimiser la taille de la réponse
 */
export const AUTEURS_BASE_FIELDS = [
  'id',
  'nom',
  'prenom',
  'identite_complete',
  'slug',
  'photo',
  'date_naissance',
  'date_mort',
  'nationalite',
  'pays_id',
  'siecles',
  'siecle_principal',
  'genre',
  'nb_livres',
] as const;

/**
 * Facettes filtrables pour les auteurs
 * Utilisé par Meilisearch pour calculer les counts de filtres
 */
export const AUTEURS_FILTERABLE_FACETS = [
  // 'pays_id',
  // 'siecles',
  // 'genre',
] as const;

// ============================================================================
// Configuration Éditeurs
// ============================================================================

/**
 * Champs de base à récupérer pour l'index éditeurs
 */
export const LIEUX_BASE_FIELDS = [
  'id',
  'nom',
  'nom_local',
  'nom_en',
  'prenom',
  'miniature_carree',
  'identite_complete',
  'fr.slug',
  'en.slug',
] as const;
/**
 * Facettes filtrables pour les éditeurs
 */
export const LIEUX_FILTERABLE_FACETS = ['pays_id', 'siecles'] as const;

/**
 * Champs de base à récupérer pour l'index éditeurs
 */
export const EDITEURS_BASE_FIELDS = [
  'id',
  'nom',
  'prenom',
  'appellation_complete',
  'slug',
  'photo',
  'ville',
  'pays_id',
  'siecles',
] as const;

/**
 * Facettes filtrables pour les éditeurs
 */
export const EDITEURS_FILTERABLE_FACETS = ['pays_id', 'siecles'] as const;

// ============================================================================
// Configuration Relieurs
// ============================================================================

/**
 * Champs de base à récupérer pour l'index relieurs
 */
export const RELIEURS_BASE_FIELDS = [
  'id',
  'nom',
  'prenom',
  'slug',
  'photo',
  'pays_id',
  'siecles',
] as const;

/**
 * Facettes filtrables pour les relieurs
 */
export const RELIEURS_FILTERABLE_FACETS = ['pays_id', 'siecles'] as const;

// ============================================================================
// Factory Functions
// ============================================================================

/**
 * Récupère les champs à interroger selon le type d'entité
 *
 * @param entityType - Type d'entité (auteurs, editeurs, relieurs)
 * @returns Liste des champs à récupérer de Meilisearch
 *
 * @example
 * ```typescript
 * const fields = getEntityFields('auteurs');
 * // ['id', 'nom', 'prenom', ...]
 * ```
 */
export function getEntityFields(entityType: EntityType): readonly string[] {
  const fieldsMap = {
    auteurs: AUTEURS_BASE_FIELDS,
    editeurs: EDITEURS_BASE_FIELDS,
    relieurs: RELIEURS_BASE_FIELDS,
    lieux_edition: LIEUX_BASE_FIELDS,
  } as const;

  return fieldsMap[entityType];
}

/**
 * Récupère les facettes filtrables selon le type d'entité
 *
 * @param entityType - Type d'entité (auteurs, editeurs, relieurs)
 * @returns Liste des attributs filtrables (avec counts)
 *
 * @example
 * ```typescript
 * const facets = getEntityFacets('auteurs');
 * // ['pays_id', 'siecles', 'genre']
 * ```
 */
export function getEntityFacets(entityType: EntityType): readonly string[] {
  const facetsMap = {
    auteurs: AUTEURS_FILTERABLE_FACETS,
    editeurs: EDITEURS_FILTERABLE_FACETS,
    relieurs: RELIEURS_FILTERABLE_FACETS,
    lieux_edition: LIEUX_FILTERABLE_FACETS,
  } as const;

  return facetsMap[entityType];
}
